/**
 * Track overlay helpers for the committee CourseViewer.
 *
 * A "track" is an ordered list of GPS fixes for a single boat, used to overlay
 * a real sailed path on top of course marks + legs so an admin can frame a
 * SignalK track against candidate courses and visually set/correct the course.
 *
 * Canonical point shape is a tuple: [lat, lon, ts?, speed?]
 *   - lat, lon : numbers (degrees)
 *   - ts       : optional ISO timestamp string
 *   - speed    : optional boat speed in knots (used for speed colouring)
 *
 * The source is the analysis pipeline's `data.json` (the `track` array there is
 * [lat, lon, ts]). Speed is optional and may be supplied as a 4th element.
 */

/** Coerce a value to a finite number, or null. */
function num(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Normalize a single fix into [lat, lon, ts?, speed?], or null if it has no
 * usable coordinates. Accepts tuple form ([lat, lon, ts, speed]) or object form
 * ({lat|latitude, lon|lng|longitude, t|ts, speed|sog|bs}).
 */
export function normalizeTrackPoint(p) {
  if (Array.isArray(p)) {
    const lat = num(p[0]);
    const lon = num(p[1]);
    if (lat === null || lon === null) return null;
    const ts = typeof p[2] === 'string' ? p[2] : null;
    const speed = num(p[3]);
    return [lat, lon, ts, speed];
  }
  if (p && typeof p === 'object') {
    const lat = num(p.lat ?? p.latitude);
    const lon = num(p.lon ?? p.lng ?? p.longitude);
    if (lat === null || lon === null) return null;
    const ts = typeof (p.t ?? p.ts) === 'string' ? (p.t ?? p.ts) : null;
    const speed = num(p.speed ?? p.sog ?? p.bs);
    return [lat, lon, ts, speed];
  }
  return null;
}

/** Parse an ISO timestamp to epoch ms, treating naive stamps as UTC. */
function tsMs(s) {
  if (typeof s !== 'string') return null;
  const ms = new Date(s + (s.includes('+') || s.includes('Z') ? '' : 'Z')).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Linear-interpolate a value from time-sorted samples at epoch ms `t`. */
function interpAt(times, values, t) {
  const n = times.length;
  if (n === 0) return null;
  if (t <= times[0]) return values[0];
  if (t >= times[n - 1]) return values[n - 1];
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (times[mid] <= t) lo = mid;
    else hi = mid;
  }
  const span = times[hi] - times[lo];
  if (span <= 0) return values[lo];
  const frac = (t - times[lo]) / span;
  return values[lo] + (values[hi] - values[lo]) * frac;
}

/**
 * Join boat speed from an analysis `inst` stream onto track fixes by timestamp.
 *
 * A bundled analysis `data.json` carries track fixes as [lat, lon, ts] and the
 * boat speed (`bs`) separately in `inst[]`. Fixes that already carry a 4th
 * speed element, or that have no timestamp, are left unchanged.
 */
export function joinInstSpeed(points, inst) {
  if (!Array.isArray(inst) || inst.length === 0) return points;
  const samples = inst
    .map((s) => ({ t: tsMs(s && (s.t ?? s.ts)), bs: num(s && (s.bs ?? s.sog ?? s.speed)) }))
    .filter((s) => s.t !== null && s.bs !== null)
    .sort((a, b) => a.t - b.t);
  if (samples.length === 0) return points;
  const times = samples.map((s) => s.t);
  const values = samples.map((s) => s.bs);
  return points.map((p) => {
    if (p[3] !== null && p[3] !== undefined) return p;
    const t = tsMs(p[2]);
    if (t === null) return p;
    return [p[0], p[1], p[2], interpAt(times, values, t)];
  });
}

/**
 * Parse raw loaded JSON into a normalized track.
 *
 * Accepts either an analysis `data.json` object (uses its `track` array, and
 * joins boat speed from its `inst` stream) or a bare array of fixes. Returns
 * { points, hasSpeed, summary } — `points` is the cleaned
 * [lat, lon, ts?, speed?] list, never null.
 */
export function parseTrackData(raw) {
  let rawPoints = [];
  let inst = null;
  if (Array.isArray(raw)) {
    rawPoints = raw;
  } else if (raw && typeof raw === 'object' && Array.isArray(raw.track)) {
    rawPoints = raw.track;
    if (Array.isArray(raw.inst)) inst = raw.inst;
  }

  let points = rawPoints.map(normalizeTrackPoint).filter(Boolean);
  if (inst) points = joinInstSpeed(points, inst);
  return {
    points,
    hasSpeed: trackHasSpeed(points),
    summary: trackSummary(points),
  };
}

/** True if any point carries a usable speed value. */
export function trackHasSpeed(points) {
  return points.some((p) => p[3] !== null && p[3] !== undefined);
}

/**
 * Colour for a boat speed (knots). Mirrors the `bsColor` scale used in the
 * racemarks.app analysis page: slow = cool, fast = warm.
 */
export function speedColor(speed) {
  if (speed === null || speed === undefined || Number.isNaN(speed)) return '#6366f1';
  if (speed < 3) return '#60a5fa';
  if (speed < 5) return '#22c55e';
  if (speed < 6.5) return '#f59e0b';
  if (speed < 8) return '#f97316';
  return '#ef4444';
}

/** Legend rows (colour, label) matching {@link speedColor}, fast → slow. */
export const SPEED_LEGEND = [
  ['#ef4444', '> 8 kn'],
  ['#f97316', '6.5–8'],
  ['#f59e0b', '5–6.5'],
  ['#22c55e', '3–5'],
  ['#60a5fa', '< 3 kn'],
];

/**
 * Break a track into drawable polylines, each `{ positions, color }`.
 *
 * Without speed data the whole track is a single solid polyline. With speed,
 * consecutive pair-segments are coloured by the mean of their endpoint speeds
 * and merged into one polyline per same-colour run — so a typical multi-
 * thousand-point track yields a handful of Leaflet layers, not thousands.
 */
export function buildTrackSegments(points, defaultColor = '#6366f1') {
  if (points.length < 2) return [];

  if (!trackHasSpeed(points)) {
    return [{ positions: points.map((p) => [p[0], p[1]]), color: defaultColor }];
  }

  const segments = [];
  let run = null;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const vals = [a[3], b[3]].filter((s) => s !== null && s !== undefined);
    const mean = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    const color = speedColor(mean);
    if (run && run.color === color) {
      run.positions.push([b[0], b[1]]);
    } else {
      run = { positions: [[a[0], a[1]], [b[0], b[1]]], color };
      segments.push(run);
    }
  }
  return segments;
}

/** Latitude/longitude bounds of the track, or null if empty. */
export function trackBounds(points) {
  if (points.length === 0) return null;
  return points.map((p) => [p[0], p[1]]);
}

/** Human-friendly summary: point count + time span. */
export function trackSummary(points) {
  const count = points.length;
  const stamped = points.map((p) => p[2]).filter(Boolean);
  let start = null;
  let end = null;
  if (stamped.length > 0) {
    start = stamped[0];
    end = stamped[stamped.length - 1];
  }
  return { count, start, end };
}
